import { Select } from '@cube3/cubicle/src/core/atoms/Select/Select';
import React, { useCallback, useMemo } from 'react';
import { Option } from '@cube3/cubicle/src/core/atoms/Select/Option';
import { getErrorMessage } from '../utils/getErrorMessage';
import { HBox, VBox } from '@cube3/cubicle/src/core/templates/layout/Flex';
import { Message } from '@cube3/cubicle/src/core/atoms/InputField/Message';
import { FieldWithState } from '../types/GenericFormTypes';

export type SelectFieldProps = FieldWithState<string, 'select'>;

const parser = (option) => {
  const opt = option || '';
  if (typeof opt === 'string') {
    return {
      id: opt,
      value: opt,
      display_name: opt
    };
  }
  return {
    ...opt,
    id: opt.id,
    value: (opt.value ||
      opt.type ||
      opt.id) /* used in `SearchBarFilter`*/ as string,
    display_name: (opt.display_name || opt.displayName || opt.id) as string
  };
};

const mappingOptions = (options = []) => {
  return options.map((opt) => parser(opt));
};

export const SelectField = (props: SelectFieldProps) => {
  const {
    required,
    //input
    value,
    onChange,
    //meta
    error,
    touched,
    invalid,
    active,
    warning,
    showErrors,
    //
    options,
    showClearButton,
    width = 80,
    ...extraProps
  } = props;

  const errorMessage = error ? getErrorMessage(error) : warning;
  const shouldError = showErrors && touched && !active && invalid;

  const mappedOptions = useMemo(() => mappingOptions(options), [options]);
  const stringOnly = typeof options?.[0] === 'string';

  const handleChange = useCallback(
    (val: string) => {
      onChange(
        stringOnly ? val : mappedOptions.find((o) => o.display_name === val)
      );
    },
    [mappedOptions]
  );

  const parsedValue = parser(value).display_name;


  return (
    <VBox gap={4}>
      <HBox>
        <Select
          {...extraProps}
          onChange={handleChange}
          value={parsedValue}
          criticalState={shouldError && invalid}
          showClearButton={showClearButton ?? !required}
          width={width}
        >
          {mappedOptions.map((opt) => {
            return <Option {...opt} key={opt.value} value={opt.display_name} />;
          })}
        </Select>
      </HBox>
      {(warning || (shouldError && errorMessage)) && (
        <Message
          label={errorMessage}
          messageColor={warning ? 'warning' : 'critical'}
        />
      )}
    </VBox>
  );
};
